import { trpc } from '@/lib/trpc/trpc';
import Link from 'next/link';
import Image from 'next/image';
import { usePermission } from '@/lib/auth/frontendAuthUtils';
import { PERMISSIONS } from '@/lib/auth/permissions';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { IconUser, IconMenu2, IconLogout } from '@tabler/icons-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { UserContext } from '@/contexts/UserContext';
import { useContext, useState } from 'react';

export default function NavBar() {
  const [openMenuSheet, setOpenMenuSheet] = useState(false);
  const userContext = useContext(UserContext);
  const userData = trpc.User.GetUser.useQuery({ id: userContext.claims?.sub });

  // Use our new permission system instead of direct scope checking
  const canAccessAircraftSettings = usePermission(
    userContext,
    PERMISSIONS.GLOBAL_ADMIN
  );

  return (
    <header className='flex items-center justify-between px-4 py-3'>
      <div className='flex items-center gap-4'>
        <div className='md:hidden'>
          <div className='flex items-center gap-4'>
            <Sheet
              open={openMenuSheet}
              onOpenChange={() => setOpenMenuSheet(!openMenuSheet)}
            >
              <SheetTrigger>
                <IconMenu2 onClick={() => setOpenMenuSheet(!openMenuSheet)} />
              </SheetTrigger>
              <SheetContent side={'left'} className='w-full bg-slate-600'>
                <SheetHeader>
                  <SheetTitle>Air Reserve Menu</SheetTitle>
                </SheetHeader>
                <div className='grid grid-cols-1 p-1'>
                  <div className='p-2'>
                    <Link
                      onClick={() => setOpenMenuSheet(!openMenuSheet)}
                      href='/calendar'
                      className='text-lg font-medium underline-offset-4 hover:underline'
                      prefetch={false}
                    >
                      Calendar
                    </Link>
                  </div>
                  <div className='p-2'>
                    <Link
                      onClick={() => setOpenMenuSheet(!openMenuSheet)}
                      href='/flightlog'
                      className='text-lg font-medium underline-offset-4 hover:underline'
                      prefetch={false}
                    >
                      Flight Log
                    </Link>
                  </div>
                  <div className='p-2'>
                    <Link
                      onClick={() => setOpenMenuSheet(!openMenuSheet)}
                      href='/statement'
                      className='text-lg font-medium underline-offset-4 hover:underline'
                      prefetch={false}
                    >
                      Your Statement
                    </Link>
                  </div>
                  <div className='p-2'>
                    <Link
                      onClick={() => setOpenMenuSheet(!openMenuSheet)}
                      href='/finances'
                      className='text-lg font-medium underline-offset-4 hover:underline'
                      prefetch={false}
                    >
                      Finances
                    </Link>
                  </div>
                  <div className='p-2'>
                    <Link
                      onClick={() => setOpenMenuSheet(!openMenuSheet)}
                      href='/performance'
                      className='text-lg font-medium underline-offset-4 hover:underline'
                      prefetch={false}
                    >
                      Aircraft Performance
                    </Link>
                  </div>
                  {canAccessAircraftSettings && (
                    <div className='p-2'>
                      <Link
                        onClick={() => setOpenMenuSheet(!openMenuSheet)}
                        href='/aircraftsettings'
                        className='text-lg font-medium underline-offset-4 hover:underline'
                        prefetch={false}
                      >
                        Aircraft Settings
                      </Link>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        <Link href='/' className='flex items-center gap-2' prefetch={false}>
          <Image
            className='dark:hidden'
            src={'/light-NavLogo.png'}
            width={150}
            height={100}
            alt='Logo'
            priority={true}
          />
          <Image
            className='hidden dark:block'
            src={'/dark-NavLogo.png'}
            width={150}
            height={100}
            alt='Logo'
            priority={true}
          />
        </Link>
        <nav className='xs:hidden hidden items-center gap-4 md:flex'>
          <Link
            href='/calendar'
            className='text-sm font-medium text-muted-foreground hover:text-foreground'
            prefetch={false}
          >
            Calendar
          </Link>
          <Link
            href='/flightlog'
            className='text-sm font-medium text-muted-foreground hover:text-foreground'
            prefetch={false}
          >
            Flight Log
          </Link>
          <Link
            href='/statement'
            className='text-sm font-medium text-muted-foreground hover:text-foreground'
            prefetch={false}
          >
            Your Statement
          </Link>
          <Link
            href='/finances'
            className='text-sm font-medium text-muted-foreground hover:text-foreground'
            prefetch={false}
          >
            Finances
          </Link>
          <Link
            href='/performance'
            className='text-sm font-medium text-muted-foreground hover:text-foreground'
            prefetch={false}
          >
            Aircraft Performance
          </Link>
          {canAccessAircraftSettings && (
            <Link
              href='/aircraftsettings'
              className='text-sm font-medium text-muted-foreground hover:text-foreground'
              prefetch={false}
            >
              Aircraft Settings
            </Link>
          )}
        </nav>
      </div>
      <div className='flex items-center gap-4'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='icon' className='rounded-full'>
              <Avatar className='h-8 w-8 text-white'>
                <AvatarImage
                  src={userData.data?.avatar || ''}
                  alt='@username'
                />
                <AvatarFallback>
                  {userData.data
                    ? userData.data.avatar
                      ? ''
                      : userData.data.profile?.givenName &&
                          userData.data.profile.familyName
                        ? `${(userData.data.profile?.givenName).substring(0, 1)}${userData.data.profile.familyName.substring(0, 1)}`
                        : userData.data.name.substring(0, 1)
                    : userContext.claims?.name?.substring(0, 1)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-56'>
            <DropdownMenuLabel>
              {userContext.claims?.name || 'Name not Set'}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href='/user'>
                <IconUser className='mr-2 h-4 w-4' />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <div
                onClick={() => window.location.assign('/api/logto/sign-out')}
              >
                <IconLogout className='mr-2 h-4 w-4' />
                Sign-Out
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
